import get from 'lodash/get';
import React from 'react';
import {connect} from 'react-redux';

import Button from 'components/Button';
import Warning from 'components/Warning';
import ErrorBlock from 'components/ErrorBlock';
import {default as Modal, withModal} from 'components/Modal';
import BookingStatus from 'components/BookingStatus';
import {ConfirmationModal} from 'components/CommonModals';
import StepSection from 'components/StepSection';

import {
  BookingType,
  Buttons,
  TenantCard,
  PaymentSummary,
  CheckList
} from 'containers/FrontDesk/components/shared';

import {formatTimeBeforeDate} from 'utils/datetime';
import {InfoSection} from './components';

import {CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN} from 'ducks/allocations';

import {
  MODALS,
  fetchRoomMove,
  completeRoomMove,
  giveAccessToRoom,
  undoGiveAccessToRoom,
  vacateRoom,
  undoVacateRoom,
  fetchPaymentSummary
} from 'ducks/frontDesk';

import {
  isModalOpen,
  openModal,
  closeModal,
  storeDataInModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';

import logInIcon from './assets/log-in.svg';
import logOutIcon from './assets/log-out.svg';

import './styles.css';

const AccessGiven = ({givenAccessBy, givenAccessAt, undoGiveAccess}) => (
  <div className="AccessGiven">
    <img src={logInIcon} alt="Log in icon" />
    <div className="text">
      Access was granted by {givenAccessBy} at{' '}
      {formatTimeBeforeDate(givenAccessAt)}
    </div>
    <Button white className="undoGiveAccessButton" onClick={undoGiveAccess}>
      Undo give access
    </Button>
  </div>
);

const giveAccessChecklistItems = [
  {
    name: 'roomCleaned',
    label: 'Is the room cleaned?',
    defaultChecked: false
  },
  {
    name: 'receivedKey',
    label: 'Received the key for their post box?',
    defaultChecked: false
  },
  {
    name: 'keyCardChanged',
    label: 'Key card changed?',
    defaultChecked: false
  }
];

const GiveAccess = ({giveAccess, tooEarlyWarning}) => (
  <React.Fragment>
    {tooEarlyWarning && <Warning>{tooEarlyWarning}</Warning>}
    <CheckList items={giveAccessChecklistItems} />
    <Button white className="giveAccessButton" onClick={giveAccess}>
      Give access
    </Button>
  </React.Fragment>
);

const Vacated = ({vacatedBy, vacatedAt, undoVacateRoom}) => (
  <div className="Vacated">
    <img src={logOutIcon} alt="Log out icon" />
    <div className="text">
      Departure from old room was marked as completed by {vacatedBy} at{'  '}
      {formatTimeBeforeDate(vacatedAt)}
    </div>
    <Button white className="undoVacateButton" onClick={undoVacateRoom}>
      Undo vacate room
    </Button>
  </div>
);

const vacateRoomChecklistItems = [
  {
    name: 'returnedKey',
    label: 'Returned the key for the post box?',
    defaultChecked: false
  },
  {
    name: 'inventorySigned',
    label: 'Inventory signed?',
    defaultChecked: false
  },
  {
    name: 'keyCardReturned',
    label: 'Current key card returned and cleared?',
    defaultChecked: false
  }
];

const VacateRoom = ({vacateRoom}) => (
  <React.Fragment>
    <CheckList items={vacateRoomChecklistItems} />
    <Button white className="vacateRoomButton" onClick={vacateRoom}>
      Vacate room
    </Button>
  </React.Fragment>
);

class CompleteRoomMoveModal extends React.Component<any> {
  componentDidMount() {
    const {data: {roomMoveId, locationRef}} = this.props;

    this.props.fetchRoomMove(
      {roomMoveId, locationRef},
      this.fetchRoomMoveExtraActions
    );
  }

  fetchRoomMoveExtraActions = [
    ...this.props.storeDataExtraActions(data => ({
      roomMove: data
    })),
    data =>
      this.props.fetchPaymentSummary(
        {
          locationRef: this.props.data.locationRef,
          bookingId: data.booking.id
        },
        [
          ...this.props.storeDataExtraActions(data => ({
            paymentSummary: data
          }))
        ]
      )
  ];

  completeRoomMove = () => {
    const {
      data: {roomMoveId, locationRef},
      fetchRoomMovesExtraAction
    } = this.props;

    const successExtraActions = [
      ...fetchRoomMovesExtraAction(),
      ...this.props.closeModalExtraActions,
      () => closeModal(BASE_MODALS.CONFIRM)
    ];

    const failureExtraActions = [
      ({errors}) =>
        storeDataInModal({
          modalName: MODALS.COMPLETE_ROOM_MOVE,
          data: {
            completeRoomMoveErrors: errors
          }
        })
    ];

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Complete room move',
      question: 'Are you sure you want to complete this room move?',
      confirmAction: () =>
        this.props.completeRoomMove(
          {roomMoveId, locationRef},
          successExtraActions,
          failureExtraActions
        )
    });
  };

  giveAccessToRoom = ({cleaningStatus, cleaningStatusDisallowed}) => {
    const {data: {roomMoveId, roomMove, locationRef}} = this.props;

    const extraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () =>
        fetchRoomMove({locationRef, roomMoveId}, this.fetchRoomMoveExtraActions)
    ];

    const confirmAction = () => {
      this.props.giveAccessToRoom({locationRef, roomMoveId}, extraActions);
    };

    const question = (
      <div>
        Are you sure you want to give access to room{' '}
        <b>{roomMove.newAllocation.room.roomNumber}</b>?
      </div>
    );

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Give Access',
      question,
      errors: cleaningStatusDisallowed
        ? {
            _error: `Allocated room is "${cleaningStatus.toLowerCase()}". Please change the room or update the room housekeeping status.`
          }
        : null,
      showQuestionOnError: true,
      confirmAction,
      confirmButtonText: 'Give Access'
    });
  };

  undoGiveAccessToRoom = () => {
    const {data: {roomMoveId, roomMove, locationRef}} = this.props;

    const extraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () =>
        fetchRoomMove({locationRef, roomMoveId}, this.fetchRoomMoveExtraActions)
    ];

    const confirmAction = () => {
      this.props.undoGiveAccessToRoom({locationRef, roomMoveId}, extraActions);
    };

    const question = (
      <div>
        Are you sure you want to undo give access to room{' '}
        <b>{roomMove.newAllocation.room.roomNumber}</b>?
      </div>
    );

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo Give Access',
      question,
      confirmAction,
      confirmButtonText: 'Undo Give Access'
    });
  };

  vacateRoom = ({cleaningStatus, cleaningStatusDisallowed}) => {
    const {data: {roomMoveId, roomMove, locationRef}} = this.props;

    const extraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () =>
        fetchRoomMove({locationRef, roomMoveId}, this.fetchRoomMoveExtraActions)
    ];

    const confirmAction = () => {
      this.props.vacateRoom({locationRef, roomMoveId}, extraActions);
    };

    const question = (
      <div>
        Are you sure you want to vacate room{' '}
        <b>{roomMove.oldAllocation.room.roomNumber}</b>?
      </div>
    );

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Vacate Room',
      question,
      errors: cleaningStatusDisallowed
        ? {
            _error: `Allocated room is "${cleaningStatus.toLowerCase()}". Please change the room or update the room housekeeping status.`
          }
        : null,
      showQuestionOnError: true,
      confirmAction,
      confirmButtonText: 'Vacate Room'
    });
  };

  undoVacateRoom = () => {
    const {data: {roomMoveId, roomMove, locationRef}} = this.props;

    const extraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () =>
        fetchRoomMove({locationRef, roomMoveId}, this.fetchRoomMoveExtraActions)
    ];

    const confirmAction = () => {
      this.props.undoVacateRoom({locationRef, roomMoveId}, extraActions);
    };

    const question = (
      <div>
        Are you sure you want to undo vacated room{' '}
        <b>{roomMove.oldAllocation.room.roomNumber}</b>?
      </div>
    );

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo Vacate Room',
      question,
      confirmAction,
      confirmButtonText: 'Undo Vacate Room'
    });
  };

  render() {
    const {
      data: {roomMove, paymentSummary, completeRoomMoveErrors},
      closeModal,
      confirmationModal
    } = this.props;

    const modalType =
      roomMove && roomMove.secondaryTenant ? Modal.MEDIUM : Modal.SUB_MEDIUM;

    const cleaningStatus = get(
      roomMove,
      'newAllocation.room.cleaningStatus',
      ''
    );

    const skipCleaningStatusCheck = get(
      roomMove,
      'newAllocation.room.skipCleaningStatusCheck',
      ''
    );

    const cleaningStatusDisallowed =
      skipCleaningStatusCheck !== true &&
      CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN.includes(cleaningStatus);

    const giveAccessHeader = roomMove && (
      <div className="giveAccessHeader">
        Give access to new room
        <p className="bold"> {`${roomMove.newAllocation.room.roomNumber}`} </p>
        after
        <p className="bold">
          {`${formatTimeBeforeDate(roomMove.newAllocation.startDate)}`}
        </p>
      </div>
    );

    const vacateOldRoomHeader = roomMove && (
      <div className="vacateOldRoomHeader">
        Vacate old room
        <p className="bold"> {`${roomMove.oldAllocation.room.roomNumber}`} </p>
        before
        <p className="bold">
          {`${formatTimeBeforeDate(roomMove.oldAllocation.endDate)}`}
        </p>
      </div>
    );

    const completeButtonActive =
      roomMove && roomMove.vacatedAt && roomMove.givenAccessAt;

    return (
      <React.Fragment>
        <Modal
          className="CompleteRoomMoveModal"
          show={true}
          type={modalType}
          onClose={closeModal}>
          <Modal.Header>
            <BookingType bookingType={get(roomMove, 'booking.bookingType')} />
            <div className="headingAndStatus">
              <h3 className="heading">
                {get(roomMove, 'booking.bookingType')} Room Change
              </h3>
              {roomMove && <BookingStatus status={roomMove.booking.status} />}
            </div>
          </Modal.Header>
          {roomMove && (
            <Modal.Body>
              <InfoSection roomMove={roomMove} />
              {cleaningStatusDisallowed && (
                <ErrorBlock className="roomStatusError">
                  {`Allocated room is "${cleaningStatus.toLowerCase()}". Please change the room or update
                    the room housekeeping status.`}
                </ErrorBlock>
              )}
              {completeRoomMoveErrors && (
                <ErrorBlock>{get(completeRoomMoveErrors, '_error')}</ErrorBlock>
              )}

              {!roomMove.booking.agreementSigned && (
                <Warning className="licenseAgreementWarning">
                  There is no completed agreement for this booking
                </Warning>
              )}

              <StepSection
                step="1"
                header="Check that the member's personal details are up to date">
                <TenantCard tenant={roomMove.primaryTenant} />
                {roomMove.secondaryTenant && (
                  <TenantCard tenant={roomMove.secondaryTenant} />
                )}
              </StepSection>

              <StepSection step="2" header="Payment summary">
                <div className="paymentSummary">
                  <PaymentSummary paymentSummary={paymentSummary} />
                  {!roomMove.primaryTenant.activeGocardlessCustomer && (
                    <Warning>
                      The member has not yet signed up for GoCardless
                    </Warning>
                  )}
                </div>
              </StepSection>

              <StepSection step="3" header={giveAccessHeader}>
                <div className="giveAccessSection">
                  {roomMove.givenAccessAt && (
                    <AccessGiven
                      givenAccessAt={roomMove.givenAccessAt}
                      givenAccessBy={roomMove.givenAccessBy.fullName}
                      undoGiveAccess={this.undoGiveAccessToRoom}
                    />
                  )}
                  {!roomMove.givenAccessAt && (
                    <GiveAccess
                      tooEarlyWarning={roomMove.tooEarlyWarning}
                      giveAccess={() =>
                        this.giveAccessToRoom({
                          cleaningStatus,
                          cleaningStatusDisallowed
                        })
                      }
                    />
                  )}
                </div>
              </StepSection>

              <StepSection step="4" header={vacateOldRoomHeader}>
                <div className="vacateOldRoomSection">
                  {roomMove.vacatedAt && (
                    <Vacated
                      vacatedAt={roomMove.vacatedAt}
                      vacatedBy={roomMove.vacatedBy.fullName}
                      undoVacateRoom={this.undoVacateRoom}
                    />
                  )}
                  {!roomMove.vacatedAt && (
                    <VacateRoom
                      vacateRoom={() =>
                        this.vacateRoom({
                          cleaningStatus,
                          cleaningStatusDisallowed
                        })
                      }
                    />
                  )}
                </div>
              </StepSection>
            </Modal.Body>
          )}
          <Modal.Footer>
            <Buttons
              closeModal={closeModal}
              onSubmit={this.completeRoomMove}
              active={completeButtonActive}
              submitting={false}
              label="Complete"
            />
          </Modal.Footer>
        </Modal>
        {confirmationModal && <ConfirmationModal />}
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  confirmationModal: isModalOpen(state, BASE_MODALS.CONFIRM)
});

const mapDispatchToProps = {
  fetchRoomMove,
  completeRoomMove,
  giveAccessToRoom,
  undoGiveAccessToRoom,
  openModal,
  vacateRoom,
  undoVacateRoom,
  fetchPaymentSummary
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withModal(MODALS.COMPLETE_ROOM_MOVE)(CompleteRoomMoveModal)
) as React.ComponentType<any>;
